/**
 * useSocket — singleton Socket.io connection.
 *
 * Connects once when a logged-in user is present and reuses the same
 * socket instance across the app. The socket is disconnected on logout.
 *
 * Usage:
 *   const { socket, isConnected } = useSocket();
 */

import { useEffect, useRef, useState } from "react";
import { io } from "socket.io-client";
import { getCookie } from "@/utils/cookie";

let globalSocket = null; // module-level singleton

export const getSocket = () => globalSocket;

const getSocketConfig = () => {
  const token = getCookie("access_token");
  const url = process.env.NEXT_PUBLIC_SOCKET_URL;

  return {
    token,
    url,
  };
};

/**
 * Creates (or reuses) the global Socket.io instance.
 * @returns {import("socket.io-client").Socket}
 */
export const initSocket = () => {
  const { token, url } = getSocketConfig();

  if (!url || !token) {
    destroySocket();
    return null;
  }

  if (globalSocket) {
    globalSocket.auth = { token };
    globalSocket.io.opts.query = {
      ...(globalSocket.io.opts.query || {}),
      token,
    };

    if (globalSocket.disconnected) {
      globalSocket.connect();
    }

    return globalSocket;
  }

  globalSocket = io(url, {
    auth: { token },
    query: { token },
    transports: ["websocket"],
    reconnectionAttempts: 5,
    reconnectionDelay: 2000,
    autoConnect: true,
    withCredentials: true,
  });

  return globalSocket;
};

/** Cleanly tear down the singleton (e.g. on logout). */
export const destroySocket = () => {
  if (globalSocket) {
    globalSocket.disconnect();
    globalSocket = null;
  }
};

/**
 * React hook that exposes the socket and its connection status.
 */
const useSocket = () => {
  const [isConnected, setIsConnected] = useState(false);
  const socketRef = useRef(null);

  useEffect(() => {
    const socket = initSocket();
    if (!socket) return;

    socketRef.current = socket;

    const onConnect = () => setIsConnected(true);
    const onDisconnect = () => setIsConnected(false);

    socket.on("connect", onConnect);
    socket.on("disconnect", onDisconnect);

    // Sync initial state
    setIsConnected(socket.connected);

    return () => {
      socket.off("connect", onConnect);
      socket.off("disconnect", onDisconnect);
    };
  }, []);

  return { socket: socketRef.current, isConnected };
};

export default useSocket;
